import { useMutation, useQueryClient } from '@tanstack/react-query';

import i18n from '@/core/i18n';
import { displayToastError, displayToastInfo, displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateAfterMemberAccessChange } from '@/modules/common/utils/query-invalidation';

import { updateMemberAccess } from '../../api/member-access.api';
import { UpdateMemberUserGroupItem } from '../../types/request/member-access-request';

/**
 * Updates team member user groups in batch.
 * Accepts already-built update items (the diff is computed by `buildUpdatePayload`
 * from the edits store, so it spans every member across all filters).
 */
export const useUpdateMemberAccess = () => {
  const queryClient = useQueryClient();
  const { getErrorMessage } = useApiError();

  return useMutation({
    mutationFn: async (items: UpdateMemberUserGroupItem[]) => {
      if (items.length === 0) {
        displayToastInfo(i18n.t('authenticated.settings:pages.members.noChanges'));
        return;
      }
      await updateMemberAccess(items);
    },
    onSuccess: async (_data, items) => {
      if (items.length > 0) {
        await invalidateAfterMemberAccessChange(queryClient);
        displayToastSuccess(i18n.t('authenticated.settings:pages.members.saveSuccess'));
      }
    },
    onError: (error: unknown) => {
      displayToastError(getErrorMessage(error, true));
    },
  });
};
